import { createSlice, createAsyncThunk, PayloadAction } from '@reduxjs/toolkit';
import { fetchItems } from '../queries';
import { ApprovalRequestType, ApprovalTemplate } from '../../types/approval-request';
import { RootState } from '../store';

interface Template {
  id: number;
  type: ApprovalRequestType;
  json: string;
}

interface TemplateState {
  templatesLookup: ApprovalTemplates;
  templatesLoading: boolean;
  templatesError: string | null;
}

const initialState: TemplateState = {
  templatesLookup: {},
  templatesLoading: false,
  templatesError: null,
};

type ApprovalTemplates = Partial<{
  [K in ApprovalRequestType]: ApprovalTemplate;
}>;

// --- Async Thunks for fetching data ---

export const fetchTemplates = createAsyncThunk<Template[]>(
  'toolbox/fetchTemplates',
  async () => {
    const data = await fetchItems('db/approval-template');
    return data;
  }
);

export const checkApprovalTemplatesLoaded = () => async (dispatch, getState) => {
  const state = getState() as RootState;
  if (Object.keys(state.approvalTemplates.templatesLookup).length === 0 && !state.approvalTemplates.templatesLoading) {
    await dispatch(fetchTemplates());
  }
};

const approvalTemplatesSlice = createSlice({
  name: 'approvalTemplates',
  initialState,
  reducers: {
    setTemplates(state, action: PayloadAction<ApprovalTemplates>) {
      state.templatesLookup = action.payload;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchTemplates.pending, (state) => {
        state.templatesLoading = true;
        state.templatesError = null;
      })
      .addCase(fetchTemplates.fulfilled, (state, action) => {
        state.templatesLookup = action.payload.reduce((acc, template) => {
          acc[template.type] = JSON.parse(template.json);
          return acc;
        }, {} as ApprovalTemplates);
        state.templatesLoading = false;
        state.templatesError = null;
      })
      .addCase(fetchTemplates.rejected, (state, action) => {
        state.templatesLoading = false;
        state.templatesError = action.error.message ?? 'Failed to fetch templates';
      });
  },
});

export default approvalTemplatesSlice.reducer;